import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import { TYPE_META, appTypeKey, appsForAffiliate, initialsFor } from './appTypes';
import { whatsNewLines, releaseHistory, downloadTarget, testBuild, appSearch } from './appDetail';
import ScreenshotStrip from './ScreenshotStrip';
import ReleaseHistory from './ReleaseHistory';
import { DownloadIcon } from './modalIcons';
import getOS from './GetOS';
import '../theme/tokens.css';
import './AppDetailModal.css';

/** How long "Link Copied!" stands before the button offers the copy again. */
const COPY_CONFIRM_MS = 1800;

const TITLE_ID = 'itcModalTitle';

/**
 * Copies `text` the way browsers did before the Clipboard API, returning whether
 * it worked. Needed because `navigator.clipboard` is exposed only on secure
 * origins — HTTPS, or the loopback names `localhost` / `127.0.0.1` / `::1` — so
 * on a plain-HTTP LAN address such as http://192.168.8.39:3000 it is undefined
 * rather than merely refusing, and every copy would silently do nothing.
 *
 * `execCommand('copy')` copies the current selection, so the text has to be sat
 * in a real, focused, selected element that is actually in the document: hiding
 * the scratch textarea with `display: none` or `hidden` would make it
 * unselectable and the copy would fail. It is parked off-screen instead, and
 * focus is handed back afterwards.
 *
 * `container` must be inside the dialog. MUI's focus trap drags focus back to
 * `.MuiDialog-container` the moment anything outside the modal takes it, which
 * collapses the selection before the copy can read it — so a scratch element
 * appended to `document.body` copies nothing at all.
 */
function copyBySelection(text, container) {
  const scratch = document.createElement('textarea');
  scratch.value = text;
  scratch.setAttribute('readonly', '');
  scratch.setAttribute('aria-hidden', 'true');
  scratch.style.position = 'fixed';
  scratch.style.top = '-1000px';
  scratch.style.opacity = '0';

  const previouslyFocused = document.activeElement;
  container.appendChild(scratch);
  scratch.focus();
  scratch.select();

  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    scratch.remove();
    if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
  }
}

function LinkIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

/** Company mark in the dark header band: real logo where there is one, initials otherwise. */
function HeaderAvatar({ app }) {
  const [logoFailed, setLogoFailed] = useState(false);
  const showLogo = Boolean(app.appLogo) && !logoFailed;

  return (
    <span className="itcModalAvatar" data-testid="itcModalAvatar">
      {showLogo ? (
        <img src={app.appLogo} alt="" onError={() => setLogoFailed(true)} />
      ) : (
        initialsFor(app.name)
      )}
    </span>
  );
}

HeaderAvatar.propTypes = {
  app: PropTypes.shape({
    name: PropTypes.string,
    appLogo: PropTypes.string,
  }).isRequired,
};

/**
 * One pill per app type the affiliate has.
 *
 * These replace the old "Other apps for this affiliate" cross-link cards: an app
 * is exactly one type, so switching type means switching app. Navigation is
 * `replace`, so a walk through an affiliate's types leaves one history entry
 * behind and browser-back closes the modal rather than stepping back through
 * the pills.
 */
function TypePills({ app, comps, pathname }) {
  const siblings = appsForAffiliate(comps, app.iAffiliateID);
  if (siblings.length === 0) return null;

  return (
    <div className="itcModalPills">
      {siblings.map((sibling) => {
        const label = TYPE_META[appTypeKey(sibling.appType)].label;

        if (sibling.navLink === app.navLink) {
          return (
            <span
              key={sibling.navLink}
              className="itcModalPill itcModalPillActive"
              data-testid="itcModalPill"
              aria-current="true"
            >
              {label}
            </span>
          );
        }

        return (
          <Link
            key={sibling.navLink}
            className="itcModalPill"
            data-testid="itcModalPill"
            to={{ pathname, search: appSearch(sibling.navLink) }}
            replace
          >
            {label}
          </Link>
        );
      })}
    </div>
  );
}

TypePills.propTypes = {
  app: PropTypes.shape({
    iAffiliateID: PropTypes.number,
    navLink: PropTypes.string.isRequired,
  }).isRequired,
  comps: PropTypes.object,
  pathname: PropTypes.string.isRequired,
};

/**
 * The app detail view, as a dialog over the grid it was opened from.
 *
 * Replaces the three near-identical per-app pages plus `ScrollDialog` and
 * `ImageList`. Which app is on show is the caller's business — it comes from the
 * `?app=` query — so this component only reports that it wants to be dismissed
 * and never navigates on its own except through the type pills.
 */
export default function AppDetailModal({ app, comps, onClose }) {
  const { pathname } = useLocation();
  // Held as the link that was copied rather than a bare flag, so switching type
  // with a pill cannot leave a confirmation standing for the previous app.
  const [copiedLink, setCopiedLink] = useState(null);
  const copyTimer = useRef(null);
  const copied = copiedLink === app.navLink;

  // Tracked by path rather than as a flag, for the same reason as the copy
  // confirmation: the modal swaps content in place, so one app's missing file
  // must not suppress the next app's photo.
  const [failedPhoto, setFailedPhoto] = useState(null);
  const showPhoto = Boolean(app.BGPhoto) && failedPhoto !== app.BGPhoto;

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  // The link is rebuilt from the router's pathname rather than read off
  // `window.location.search`, so it is always this app's link and carries no
  // other query state along with it.
  const deepLink = `${window.location.origin}${pathname}${appSearch(app.navLink)}`;

  function handleCopy(event) {
    // The scratch element is anchored next to the button that was clicked, which
    // keeps it inside the dialog and so inside the focus trap. See copyBySelection.
    const container = event.currentTarget.parentNode;

    function confirmCopy() {
      setCopiedLink(app.navLink);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopiedLink(null), COPY_CONFIRM_MS);
    }

    // On a secure origin the Clipboard API confirms optimistically and undoes the
    // confirmation if the write turns out to have been refused. The fallback is
    // synchronous, so there it is only confirmed once the copy has reported back.
    if (navigator.clipboard) {
      navigator.clipboard.writeText(deepLink).catch(() => setCopiedLink(null));
      confirmCopy();
      return;
    }

    if (copyBySelection(deepLink, container)) confirmCopy();
  }

  const primary = downloadTarget(app, getOS());
  const testDownload = testBuild(app);
  const changelog = whatsNewLines(app.versionInfo);
  const history = releaseHistory(app.previousVersionInfo);
  const externalProps = primary.external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby={TITLE_ID}
      maxWidth={false}
      className="itcModalRoot"
      slotProps={{
        paper: { className: 'itcModalPaper' },
        backdrop: { className: 'itcModalBackdrop' },
      }}
    >
      <div className="itcModalHeader">
        {showPhoto && (
          <img
            className="itcModalPhoto"
            data-testid="itcModalPhoto"
            src={app.BGPhoto}
            alt=""
            aria-hidden="true"
            onError={() => setFailedPhoto(app.BGPhoto)}
          />
        )}

        <div className="itcModalHeaderActions">
          <button type="button" className="itcModalTextButton" onClick={handleCopy}>
            <LinkIcon />
            {copied ? 'Link Copied!' : 'Copy Link'}
          </button>
          <button
            type="button"
            className="itcModalIconButton"
            onClick={onClose}
            aria-label="Close app details"
          >
            <CloseIcon />
          </button>
        </div>

        <div className="itcModalIdentity">
          <HeaderAvatar app={app} />
          <div>
            <h2 className="itcModalTitle" id={TITLE_ID}>
              {app.name}
            </h2>
            <TypePills app={app} comps={comps} pathname={pathname} />
          </div>
        </div>

        <div className="itcModalDownloads">
          <a className="itcModalPrimary" href={primary.href} {...externalProps}>
            <DownloadIcon />
            {primary.label}
          </a>

          {testDownload && (
            <a className="itcModalGhost" href={testDownload.href}>
              <DownloadIcon />
              Download Test Build v{testDownload.version}
            </a>
          )}

          <p className="itcModalMeta">
            v{app.versionNo} · Updated {app.releaseDate}
          </p>
        </div>
      </div>

      <div className="itcModalBody">
        <ScreenshotStrip
          key={app.navLink}
          screenshots={app.screenshots}
          appName={app.name}
        />

        <div className="itcModalColumns">
          <div className="itcModalColumn">
            {changelog.length > 0 && (
              <section className="itcModalCard">
                <h3 className="itcModalCardTitle">What&apos;s New</h3>
                <ul className="itcModalBullets">
                  {changelog.map((line, index) => (
                    <li key={`${index}-${line}`}>
                      <span className="itcModalBulletDash" aria-hidden="true">
                        –
                      </span>
                      <span>{line}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="itcModalCard">
              <h3 className="itcModalCardTitle">About This App</h3>
              <p className="itcModalAbout">{app.aboutApp}</p>
            </section>
          </div>

          {/* Keyed on the app, so its page resets when the modal swaps in
              another one — the same reason the strip above is keyed. */}
          <ReleaseHistory key={app.navLink} releases={history} />
        </div>
      </div>
    </Dialog>
  );
}

AppDetailModal.propTypes = {
  app: PropTypes.shape({
    name: PropTypes.string.isRequired,
    navLink: PropTypes.string.isRequired,
    iAffiliateID: PropTypes.number,
    appLogo: PropTypes.string,
    BGPhoto: PropTypes.string,
    aboutApp: PropTypes.string,
    versionNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    versionInfo: PropTypes.string,
    releaseDate: PropTypes.string,
    downloadLink: PropTypes.string,
    testingLink: PropTypes.string,
    testingVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    previousVersionInfo: PropTypes.object,
    screenshots: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  comps: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};
